import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { _ } from '_locales';
import { connect } from 'react-redux';
import { colors } from '_styles';
import { useDebouncedCallback } from 'use-debounce';
import { Input } from '../../../components';
import Loader from '../../ZoneSetupScreens/components/ZoneSetupSearch/Loader';
import List from '../../ZoneSetupScreens/components/ZoneSetupSearch/components';
import { cts_secure } from '../../../app/cts';
import apis from '../../../app/apis';
import { Button } from '../../Education/components';

const inputStyle = {
  width: '100%',
  border: 'none',
  backgroundColor: '#f3f2f2',
  fontSize: '14px',
  color: '#000',
  fontWeight: '400',
  padding: '23px',
  boxSizing: 'border-box',
  lineHeight: '15px',
};

const submitInputStyle = {
  width: '100%',
  border: 'none',
  fontSize: '14px',
  color: '#f3f2f2',
  fontWeight: '400',
  padding: '23px 9px',
  boxSizing: 'border-box',
  margin: '0',
  lineHeight: '0',
  cursor: 'pointer',
};

const parentWidth = document
  .getElementById('rcroot')
  .getBoundingClientRect().width;

const AddressSearch = ({
  customColors,
  config,
  plugin,
  zone,
  searchByDefault = false,
  setCurrentPage,
  currentPage,
  setAddressDetails,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { main_color } = customColors;
  const [contentLoading, setContentLoading] = useState(false);
  const [address, setAddress] = useState([]);
  const [error, setError] = useState(false);

  const [loading, setLoading] = useState(false);
  const [selectLocationManually, setSelectLocationManually] = useState(searchByDefault);
  const { districtId, webappConfig } = config;
  const { zoneStringBpOnly = '', fullAddressBpOnly = '' } = webappConfig;
  const { zoneStr, fullAddress, exampleAddress } = zone;
  const { projectId } = plugin;

  const updatedZoneStr = zoneStringBpOnly || zoneStr;
  const updateFullAddress = fullAddressBpOnly || fullAddress;

  const fetchAddressResult = (term) => {
    const findingAddress = async () => {
      try {
        setContentLoading(true);

        const { data, status } = await cts_secure(
          'get',
          apis.getZoneJson(projectId, districtId, searchTerm)
        );

        if (status !== 200 && !data && !data.results.length) {
          throw new Error(
            'Error fetching result of addresses, in zone setup search section.'
          );
        }

        setAddress(data.results.length ? data.results : '');
        setError(!data.results.length);

        return undefined;
      } catch (e) {
        setAddress('');
        setError(true);
        return Promise.reject(e.message);
      } finally {
        setContentLoading(false);
      }
    };

    if (term.length && term.length >= 3) findingAddress();
  };

  /**
   * Prevent race Condition
   */
  const [debouncedFetchResult] = useDebouncedCallback(fetchAddressResult, 1000);

  const onChange = async ({ target }) => {
    setSearchTerm(target.value);
    setError('');
    debouncedFetchResult(target.value);
  };

  const checkEligibilityHandler = async (district_id, zoneString, address) => {
    try {
      setLoading(true);
      const { data } = await cts_secure(
        'get',
        apis.getBpServiceEligibility(projectId, district_id, zoneString)
      );

      setLoading(false);
      if (data.success) {
        setAddressDetails({
          projectId,
          districtId: district_id,
          zoneString,
          address,
        });
        setError('');
        setCurrentPage(currentPage + 1);
      } else {
        setError(data.message);
      }
      return undefined;
    } catch (e) {
      setLoading(false);
      setError('');
      return Promise.reject(e);
    }
  };

  return (
    <Container>
      <Wrapper>
        {updatedZoneStr && districtId && projectId && !selectLocationManually ? (
          <>
            <TextHeader margin="0">Confirm Location</TextHeader>
            <Text>{_('bulkPickup.please_confirm')}</Text>
            <ZoneContent>{updateFullAddress}</ZoneContent>
            <Error>{error}</Error>
            <View style={{ display: 'flex', gap: 20, marginTop: 20 }}>
              <Button
                backgroundColor="fff"
                color={main_color || colors.RC_ACTION}
                aria-label={'Schedule a Pick-up Appointment'}
                onClick={() => {
                  setSelectLocationManually(true);
                  setError('');
                }}
              >
                {_('bulkPickup.change')}
              </Button>
              <Button
                mainColor={main_color || false}
                aria-label={'Confirm Location'}
                onClick={() => checkEligibilityHandler(
                  districtId,
                  updatedZoneStr,
                  updateFullAddress
                )}
              >
                {_('bulkPickup.confirm')}
              </Button>
            </View>
          </>
        ) : (
          <>
            <TextHeader margin="0px 0px 20px 5px !important">
              {_('bulkPickup.enter_pickup_address')}
            </TextHeader>

            <SearchInputContainer>
              <label htmlFor="address-search" style={{ display: 'none' }}>
                {_('wgw.search_for_recycling_and_disposal_information')}
              </label>
              <Input
                type="text"
                id="address-search"
                style={inputStyle}
                onChange={(e) => onChange(e)}
                placeholder={`${_('zone.street_name')} ${exampleAddress ? `(e.g. ${exampleAddress})` : ''
                  }`}
              />
            </SearchInputContainer>
            <ConfirmInputContainer>
              <label htmlFor="submit-wgw" style={{ display: 'none' }}>
                {_('wgw.search')}
              </label>
              <Input
                type="submit"
                id="submit-wgw"
                style={{
                  ...submitInputStyle,
                  backgroundColor: main_color
                    ? `#${main_color}`
                    : colors.RC_ACTION,
                }}
                value={_('wgw.search')}
                ariaLabel={_('wgw.search')}
              />
            </ConfirmInputContainer>
            {error && <Error>{error}</Error>}
            <>
              {contentLoading || loading ? (
                <Loader />
              ) : (
                <>
                  {address && !error && address.length ? (
                    <List
                      term={searchTerm}
                      address={address}
                      onAddressItemClick={(item) => checkEligibilityHandler(
                        item.district_id,
                        `zone-${Object.values(item.zones).join('-')}`,
                        item.address
                      )}
                    />
                  ) : null}
                </>
              )}
            </>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

const mapStateToProps = (store) => ({
  customColors: store.config.templateColour
    ? JSON.parse(store.config.templateColour)
    : {},
  config: store.config,
  plugin: store.plugin,
  zone: store.zone,
});

export default connect(mapStateToProps, null)(AddressSearch);

const Text = styled.p``;
const View = styled.div``;
const Wrapper = styled.div`
  margin: 15px auto;
  max-width: 600px;
`;

const TextHeader = styled.h3`
  letter-spacing: 1px;
  cursor: pointer;
  margin: ${(props) => `${props.margin}`};
  font-size: 16px !important;
  color: rgb(64, 64, 64) !important;
  font-weight: 600 !important;
  font-family: Roboto, sans-serif !important;
`;

const Container = styled.div`
  box-sizing: border-box;
  padding: 20px;
  border-radius: 10px;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 18px 0px;
  margin: 10px 0;
  min-height: 150px;
`;

const SearchInputContainer = styled.div`
  width: calc(100% - 185px);
  float: left;
  margin-top: 3px;
  border-radius: 15px 0 0 15px;

  input {
    border-radius: 15px 0 0 15px;
    font-family: Roboto, sans-serif !important;

    &:focus {
      outline: none !important;
      box-shadow: 0 0 2px 2px #51a7e8;
      border-radius: 15px 0 0 15px;
    }
  }

  @media screen and (max-width: 500px) {
    width: 100%;
    border-radius: 15px;
    float: initial;

    input {
      border-radius: 15px;

      &:focus {
        outline: none !important;
        box-shadow: 0 0 2px 2px #51a7e8;
        border-radius: 15px;
      }
    }
  }

  ${parentWidth < 501
  && css`
    width: 100%;
    border-radius: 15px;
    float: initial;

    input {
      border-radius: 15px;

      &:focus {
        outline: none !important;
        box-shadow: 0 0 2px 2px #51a7e8;
        border-radius: 15px;
      }
    }
  `};
`;

const ConfirmInputContainer = styled.div`
  width: 185px;
  border-radius: 0 15px 15px 0;
  margin-top: 3px;
  display: inline-block;

  input {
    border-radius: 0 15px 15px 0;

    &:focus {
      outline: none !important;
      box-shadow: 0 0 2px 2px #51a7e8;
    }
  }

  @media screen and (max-width: 500px) {
    display: none;

    label,
    input {
      display: none;
    }
  }

  ${parentWidth < 501
  && css`
    display: none;

    label,
    input {
      display: none;
    }
  `};
`;

const ZoneContent = styled.p`
  padding: 0;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  font-family: Roboto, sans-serif;
  text-decoration: underline;
`;

const Error = styled.p`
  color: rgb(178, 1, 7);
  margin: 10px 0 0 0;
  font-size: 13px;
`;
